import type { IGlobalSearchStaticEntry } from "../types/global-search";
import {
  EGlobalSearchGate,
  type TOrganizationSettingsTabValue,
  type TProfileSettingsTabValue,
} from "../types/global-search-index";
import {
  buildGlobalSearchOrganizationTabEntry,
  buildGlobalSearchProfileTabEntry,
} from "./global-search-index-builders";
import {
  ORGANIZATION_SETTINGS_TAB_VALUES as ORG_TAB,
  PROFILE_SETTINGS_TAB_VALUES as PROFILE_TAB,
} from "./navigation-tabs";

// NOTE: NSAI-6588 — one SETTINGS_TAB entry per settings tab, keyed by the tab
// value so the compiler refuses a tab without an entry. Titles are the tab
// titles both clients already render (settings:profileTabs.* /
// settings:organizationTabs.*); gates mirror the tab lists in web's
// settings/profile/index.tsx:141-175 and settings/organization/details/
// index.tsx:351-410, plus PLATFORM_WEB where mobile has no such tab and
// PLATFORM_MOBILE where web has none.
export const GLOBAL_SEARCH_PROFILE_TAB_ENTRIES: Readonly<
  Record<TProfileSettingsTabValue, IGlobalSearchStaticEntry>
> = {
  [PROFILE_TAB.GENERAL_INFORMATION]: buildGlobalSearchProfileTabEntry({
    tab: PROFILE_TAB.GENERAL_INFORMATION,
    titleKey: "settings:profileTabs.generalInformation",
    gates: [],
  }),
  [PROFILE_TAB.BILLING]: buildGlobalSearchProfileTabEntry({
    tab: PROFILE_TAB.BILLING,
    titleKey: "settings:profileTabs.billing",
    gates: [
      EGlobalSearchGate.PLATFORM_WEB,
      EGlobalSearchGate.BILLING_AVAILABLE,
    ],
  }),
  [PROFILE_TAB.PREFERENCES]: buildGlobalSearchProfileTabEntry({
    tab: PROFILE_TAB.PREFERENCES,
    titleKey: "settings:profileTabs.preferences",
    gates: [],
  }),
  [PROFILE_TAB.MEMORY_INSTRUCTIONS]: buildGlobalSearchProfileTabEntry({
    tab: PROFILE_TAB.MEMORY_INSTRUCTIONS,
    titleKey: "settings:profileTabs.memoryInstructions",
    gates: [],
  }),
  [PROFILE_TAB.MEMORY]: buildGlobalSearchProfileTabEntry({
    tab: PROFILE_TAB.MEMORY,
    titleKey: "settings:profileTabs.memory",
    gates: [EGlobalSearchGate.USER_MEMORY_ENABLED],
  }),
  [PROFILE_TAB.CALENDAR]: buildGlobalSearchProfileTabEntry({
    tab: PROFILE_TAB.CALENDAR,
    titleKey: "settings:profileTabs.calendar",
    gates: [EGlobalSearchGate.CALL_ASSISTANT_ENABLED],
  }),
  [PROFILE_TAB.API_KEYS]: buildGlobalSearchProfileTabEntry({
    tab: PROFILE_TAB.API_KEYS,
    titleKey: "settings:profileTabs.apiKeys",
    gates: [EGlobalSearchGate.EXTERNAL_API_ENABLED],
  }),
  [PROFILE_TAB.BOT_SETTINGS]: buildGlobalSearchProfileTabEntry({
    tab: PROFILE_TAB.BOT_SETTINGS,
    titleKey: "settings:profileTabs.botSettings",
    gates: [EGlobalSearchGate.BOT_SETTINGS_ENABLED],
  }),
  [PROFILE_TAB.HISTORY]: buildGlobalSearchProfileTabEntry({
    tab: PROFILE_TAB.HISTORY,
    titleKey: "settings:profileTabs.history",
    gates: [],
  }),
  [PROFILE_TAB.TOKEN_USAGE]: buildGlobalSearchProfileTabEntry({
    tab: PROFILE_TAB.TOKEN_USAGE,
    titleKey: "settings:profileTabs.tokenUsage",
    gates: [
      EGlobalSearchGate.TOKEN_USAGE_ENABLED,
      EGlobalSearchGate.ANALYTICS_READ,
    ],
  }),
  [PROFILE_TAB.TOKEN_COST_USAGE]: buildGlobalSearchProfileTabEntry({
    tab: PROFILE_TAB.TOKEN_COST_USAGE,
    titleKey: "settings:profileTabs.tokenCostUsage",
    gates: [
      EGlobalSearchGate.TOKEN_COST_USAGE_ENABLED,
      EGlobalSearchGate.ANALYTICS_READ,
    ],
  }),
  [PROFILE_TAB.ABOUT]: buildGlobalSearchProfileTabEntry({
    tab: PROFILE_TAB.ABOUT,
    titleKey: "settings:profileTabs.about",
    gates: [EGlobalSearchGate.PLATFORM_MOBILE],
  }),
};

export const GLOBAL_SEARCH_ORGANIZATION_TAB_ENTRIES: Readonly<
  Record<TOrganizationSettingsTabValue, IGlobalSearchStaticEntry>
> = {
  [ORG_TAB.GENERAL]: buildGlobalSearchOrganizationTabEntry({
    tab: ORG_TAB.GENERAL,
    titleKey: "settings:organizationTabs.general",
    gates: [],
  }),
  [ORG_TAB.PROJECTS]: buildGlobalSearchOrganizationTabEntry({
    tab: ORG_TAB.PROJECTS,
    titleKey: "settings:organizationTabs.projects",
    gates: [EGlobalSearchGate.ORG_PROJECT_READ],
  }),
  [ORG_TAB.ROLES]: buildGlobalSearchOrganizationTabEntry({
    tab: ORG_TAB.ROLES,
    titleKey: "settings:organizationTabs.roles",
    gates: [EGlobalSearchGate.ORG_ROLE_READ],
  }),
  [ORG_TAB.MEMBERS]: buildGlobalSearchOrganizationTabEntry({
    tab: ORG_TAB.MEMBERS,
    titleKey: "settings:organizationTabs.members",
    gates: [],
  }),
  [ORG_TAB.API_KEYS]: buildGlobalSearchOrganizationTabEntry({
    tab: ORG_TAB.API_KEYS,
    titleKey: "settings:organizationTabs.apiKeys",
    gates: [
      EGlobalSearchGate.ORG_API_KEY_READ,
      EGlobalSearchGate.EXTERNAL_API_ENABLED,
    ],
  }),
  [ORG_TAB.MODELS]: buildGlobalSearchOrganizationTabEntry({
    tab: ORG_TAB.MODELS,
    titleKey: "settings:organizationTabs.models",
    gates: [EGlobalSearchGate.ORG_OWNER],
  }),
  [ORG_TAB.INTEGRATION]: buildGlobalSearchOrganizationTabEntry({
    tab: ORG_TAB.INTEGRATION,
    titleKey: "settings:organizationTabs.integration",
    gates: [
      EGlobalSearchGate.ORG_OWNER,
      EGlobalSearchGate.ORG_CONFIGURABLE_INTEGRATION,
    ],
  }),
  [ORG_TAB.CONNECTOR_APPROVAL]: buildGlobalSearchOrganizationTabEntry({
    tab: ORG_TAB.CONNECTOR_APPROVAL,
    titleKey: "settings:organizationTabs.connectorApproval",
    gates: [EGlobalSearchGate.ORG_OWNER],
  }),
  [ORG_TAB.SAML]: buildGlobalSearchOrganizationTabEntry({
    tab: ORG_TAB.SAML,
    titleKey: "settings:organizationTabs.saml",
    gates: [EGlobalSearchGate.ORG_OWNER],
  }),
  [ORG_TAB.CHAT_SETTINGS]: buildGlobalSearchOrganizationTabEntry({
    tab: ORG_TAB.CHAT_SETTINGS,
    titleKey: "settings:organizationTabs.chatSettings",
    gates: [EGlobalSearchGate.ORG_OWNER],
  }),
  [ORG_TAB.DATA_RETENTION]: buildGlobalSearchOrganizationTabEntry({
    tab: ORG_TAB.DATA_RETENTION,
    titleKey: "settings:organizationTabs.dataRetention",
    gates: [EGlobalSearchGate.ORG_OWNER],
  }),
  [ORG_TAB.SYNCATOR_API_KEYS]: buildGlobalSearchOrganizationTabEntry({
    tab: ORG_TAB.SYNCATOR_API_KEYS,
    titleKey: "settings:organizationTabs.syncatorApiKeys",
    gates: [
      EGlobalSearchGate.ORG_OWNER,
      EGlobalSearchGate.SYNCATOR_API_ENABLED,
    ],
  }),
  [ORG_TAB.DOC_RECOGNITION]: buildGlobalSearchOrganizationTabEntry({
    tab: ORG_TAB.DOC_RECOGNITION,
    titleKey: "settings:organizationTabs.docRecognition",
    gates: [
      EGlobalSearchGate.PLATFORM_WEB,
      EGlobalSearchGate.ORG_OWNER,
      EGlobalSearchGate.DOC_RECOGNITION_ENABLED,
    ],
  }),
  [ORG_TAB.APPEARANCE]: buildGlobalSearchOrganizationTabEntry({
    tab: ORG_TAB.APPEARANCE,
    titleKey: "settings:organizationTabs.appearance",
    gates: [
      EGlobalSearchGate.ORG_OWNER,
      EGlobalSearchGate.ORG_APPEARANCE_AVAILABLE,
    ],
  }),
  [ORG_TAB.SIEM_EXPORT]: buildGlobalSearchOrganizationTabEntry({
    tab: ORG_TAB.SIEM_EXPORT,
    titleKey: "settings:organizationTabs.siemExport",
    gates: [
      EGlobalSearchGate.ORG_SIEM_CONFIG_READ,
      EGlobalSearchGate.SIEM_EXPORT_ENABLED,
    ],
  }),
  [ORG_TAB.MEMORY_POLICY]: buildGlobalSearchOrganizationTabEntry({
    tab: ORG_TAB.MEMORY_POLICY,
    titleKey: "settings:organizationTabs.memoryPolicy",
    gates: [EGlobalSearchGate.ORG_MEMORY_POLICY_READ],
  }),
  [ORG_TAB.CALL_ASSISTANT]: buildGlobalSearchOrganizationTabEntry({
    tab: ORG_TAB.CALL_ASSISTANT,
    titleKey: "settings:organizationTabs.callAssistant",
    gates: [
      EGlobalSearchGate.ORG_OWNER,
      EGlobalSearchGate.CALL_ASSISTANT_ENABLED,
    ],
  }),
  [ORG_TAB.TOKEN_USAGE]: buildGlobalSearchOrganizationTabEntry({
    tab: ORG_TAB.TOKEN_USAGE,
    titleKey: "settings:organizationTabs.tokenUsage",
    gates: [EGlobalSearchGate.ORG_OWNER, EGlobalSearchGate.TOKEN_USAGE_ENABLED],
  }),
  [ORG_TAB.TOKEN_COST_USAGE]: buildGlobalSearchOrganizationTabEntry({
    tab: ORG_TAB.TOKEN_COST_USAGE,
    titleKey: "settings:organizationTabs.tokenCostUsage",
    gates: [
      EGlobalSearchGate.ORG_OWNER,
      EGlobalSearchGate.TOKEN_COST_USAGE_ENABLED,
    ],
  }),
  [ORG_TAB.USAGE_LIMITS]: buildGlobalSearchOrganizationTabEntry({
    tab: ORG_TAB.USAGE_LIMITS,
    titleKey: "settings:organizationTabs.usageLimits",
    gates: [
      EGlobalSearchGate.ORG_USAGE_LIMITS_READ,
      EGlobalSearchGate.TOKEN_LIMITS_ENABLED,
    ],
  }),
  [ORG_TAB.HISTORY]: buildGlobalSearchOrganizationTabEntry({
    tab: ORG_TAB.HISTORY,
    titleKey: "settings:organizationTabs.history",
    gates: [],
  }),
};
